import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
export const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Helper to format API errors
export const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Auth API
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  register: (data) => api.post('/auth/register', data),
  refresh: () => api.post('/auth/refresh')
};

// Users API
export const usersApi = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`)
};

// Clients API
export const clientsApi = {
  list: (params) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  activate: (id) => api.post(`/clients/${id}/activate`),
  uploadDocument: (id, file, docType) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/clients/${id}/documents?doc_type=${encodeURIComponent(docType)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  downloadDocument: (clientId, docId) => api.get(`/clients/${clientId}/documents/${docId}/download`, { responseType: 'blob' }),
  deleteDocument: (clientId, docId) => api.delete(`/clients/${clientId}/documents/${docId}`),
  verifyDocument: (clientId, docId, status, note) => api.put(`/clients/${clientId}/documents/${docId}/verification`, { verification_status: status, note: note || '' }),
  applyOcr: (clientId, extractedFields) => api.post(`/clients/${clientId}/apply-ocr`, { extracted_fields: extractedFields }),
  updateCreditScore: (id, data) => api.put(`/clients/${id}/credit-score`, data)
};

// Loan Products API
export const loanProductsApi = {
  list: () => api.get('/loan-products'),
  create: (data) => api.post('/loan-products', data),
  update: (id, data) => api.put(`/loan-products/${id}`, data)
};

// Loans API
export const loansApi = {
  list: (params) => api.get('/loans', { params }),
  get: (id) => api.get(`/loans/${id}`),
  create: (data) => api.post('/loans', data),
  approve: (id) => api.post(`/loans/${id}/approve`),
  reject: (id, reason) => api.post(`/loans/${id}/reject`, { reason }),
  disburse: (id) => api.post(`/loans/${id}/disburse`),
  repayment: (id, data) => api.post(`/loans/${id}/repayment`, data)
};

// Savings Products API
export const savingsProductsApi = {
  list: () => api.get('/savings-products'),
  create: (data) => api.post('/savings-products', data)
};

// Savings Accounts API
export const savingsAccountsApi = {
  list: (params) => api.get('/savings-accounts', { params }),
  get: (id) => api.get(`/savings-accounts/${id}`),
  create: (data) => api.post('/savings-accounts', data),
  deposit: (id, data) => api.post(`/savings-accounts/${id}/deposit`, data),
  withdraw: (id, data) => api.post(`/savings-accounts/${id}/withdraw`, data)
};

// Chart of Accounts API
export const chartOfAccountsApi = {
  list: () => api.get('/chart-of-accounts'),
  create: (data) => api.post('/chart-of-accounts', data),
  update: (id, data) => api.put(`/chart-of-accounts/${id}`, data)
};

// Journal Entries API
export const journalEntriesApi = {
  list: (params) => api.get('/journal-entries', { params }),
  create: (data) => api.post('/journal-entries', data),
  approve: (id) => api.post(`/journal-entries/${id}/approve`),
  reject: (id, reason) => api.post(`/journal-entries/${id}/reject`, { reason }),
  reverse: (id, data) => api.post(`/journal-entries/${id}/reverse`, data),
  autoGenerated: (params) => api.get('/journal-entries/auto-generated', { params }),
};

// SARB Rates API
export const sarbRatesApi = {
  list: (params) => api.get('/sarb-rates', { params }),
  current: () => api.get('/sarb-rates/current'),
  create: (data) => api.post('/sarb-rates', data),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/sarb-rates/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

// Regulatory Reports API
export const regulatoryReportsApi = {
  list: () => api.get('/regulatory-reports'),
  generateBA100: (period) => api.get('/regulatory-reports/ba100/generate', { params: { reporting_period: period } }),
  save: (data) => api.post('/regulatory-reports', data),
  submit: (id) => api.post(`/regulatory-reports/${id}/submit`)
};

// Audit Logs API
export const auditLogsApi = {
  list: (params) => api.get('/audit-logs', { params })
};

// Dashboard API
export const dashboardApi = {
  stats: () => api.get('/dashboard/stats'),
  timeSeries: () => api.get('/dashboard/time-series'),
  executive: {
    portfolioHealth: () => api.get('/executive/portfolio-health'),
    regionalBreakdown: () => api.get('/executive/regional-breakdown'),
    depositGrowth: () => api.get('/executive/deposit-growth'),
    keyRatios: () => api.get('/executive/key-ratios'),
    branchDetail: (branchId) => api.get(`/executive/branch-detail/${branchId}`),
    loanOfficerStats: () => api.get('/executive/loan-officer-stats'),
    radarData: () => api.get('/executive/radar-data'),
  },
  creditScoreConfig: {
    get: () => api.get('/credit-score-config'),
    update: (data) => api.put('/credit-score-config', data),
    evaluate: (clientId) => api.get(`/credit-score-config/evaluate/${clientId}`),
    runNow: () => api.post('/credit-score-config/run-now'),
    rescoreClient: (clientId) => api.post(`/credit-score-config/rescore/${clientId}`),
  },
  interestRateFormula: () => api.get('/interest-rate-formula'),
};

// Groups API
export const groupsApi = {
  list: () => api.get('/groups'),
  create: (data) => api.post('/groups', data)
};

// Offices API
export const officesApi = {
  list: (params) => api.get('/offices', { params }),
  hierarchy: () => api.get('/offices/hierarchy'),
  get: (id) => api.get(`/offices/${id}`),
  create: (data) => api.post('/offices', data),
  update: (id, data) => api.put(`/offices/${id}`, data),
  clients: (id) => api.get(`/offices/${id}/clients`),
  loans: (id) => api.get(`/offices/${id}/loans`),
  summary: (id) => api.get(`/offices/${id}/summary`)
};

// Employees API
export const employeesApi = {
  list: (params) => api.get('/employees', { params }),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data)
};

// Centers API
export const centersApi = {
  list: (params) => api.get('/centers', { params }),
  create: (data) => api.post('/centers', data)
};

// Payment Types API
export const paymentTypesApi = {
  list: () => api.get('/payment-types'),
  create: (data) => api.post('/payment-types', data),
  update: (id, data) => api.put(`/payment-types/${id}`, data),
  delete: (id) => api.delete(`/payment-types/${id}`)
};

// Charges API
export const chargesApi = {
  list: (params) => api.get('/charges', { params }),
  create: (data) => api.post('/charges', data),
  update: (id, data) => api.put(`/charges/${id}`, data),
  delete: (id) => api.delete(`/charges/${id}`)
};

// Holidays API
export const holidaysApi = {
  list: (params) => api.get('/holidays', { params }),
  create: (data) => api.post('/holidays', data),
  delete: (id) => api.delete(`/holidays/${id}`)
};

// Working Days API
export const workingDaysApi = {
  get: () => api.get('/working-days'),
  update: (data) => api.put('/working-days', data)
};

// Currencies API
export const currenciesApi = {
  list: () => api.get('/currencies'),
  add: (data) => api.post('/currencies', data),
  remove: (code) => api.delete(`/currencies/${code}`)
};

// Roles API
export const rolesApi = {
  list: () => api.get('/roles'),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data)
};

// Search API
export const searchApi = {
  search: (q) => api.get('/search', { params: { q } })
};

// Approvals API
export const approvalsApi = {
  list: (params) => api.get('/approvals', { params }),
  submit: (data) => api.post('/approvals', data),
  approve: (id, data) => api.put(`/approvals/${id}/approve`, data || {}),
  reject: (id, data) => api.put(`/approvals/${id}/reject`, data || {}),
  stats: () => api.get('/approvals/stats')
};

// System Settings
export const systemSettingsApi = {
  get: () => api.get('/system-settings'),
  getPublic: () => api.get('/system-settings/public'),
  update: (data) => api.put('/system-settings', data),
};

// Admin Password Reset
export const passwordResetApi = {
  adminReset: (userId, resetRequestId) => api.post('/auth/admin-reset-password', { user_id: userId, reset_request_id: resetRequestId }),
  request: (email) => api.post('/auth/request-password-reset', { email }),
  listRequests: () => api.get('/auth/password-reset-requests'),
  dismissRequest: (reqId) => api.post(`/auth/password-reset-requests/${reqId}/dismiss`),
  changeOwn: (currentPassword, newPassword) => api.post('/auth/change-password', {
    current_password: currentPassword, new_password: newPassword,
  }),
};

// Mobile Payments
export const mobilePaymentsApi = {
  receive: (data) => api.post('/mobile-payments/receive', data),
  disburse: (data) => api.post('/mobile-payments/disburse', data),
  list: (params) => api.get('/mobile-payments', { params }),
  get: (id) => api.get(`/mobile-payments/${id}`),
  clientTransactions: (clientId) => api.get(`/mobile-payments/client/${clientId}`),
};

// Financial Reports
export const reportsApi = {
  trialBalance: () => api.get('/reports/trial-balance'),
  incomeStatement: () => api.get('/reports/income-statement'),
  balanceSheet: () => api.get('/reports/balance-sheet'),
  loanPortfolioSummary: () => api.get('/reports/loan-portfolio-summary'),
  delinquencyAging: () => api.get('/reports/delinquency-aging'),
  collectionSheet: () => api.get('/reports/collection-sheet'),
  expectedPayments: () => api.get('/reports/expected-payments'),
  writtenOffLoans: () => api.get('/reports/written-off-loans'),
  clientSavingsSummary: () => api.get('/reports/client-savings-summary'),
  downloadPdf: (reportSlug) => api.get(`/reports/${reportSlug}/pdf`, { responseType: 'blob' }),
  downloadExcel: (reportSlug) => api.get(`/reports/${reportSlug}/excel`, { responseType: 'blob' }),
  downloadCsv: (reportSlug) => api.get(`/reports/${reportSlug}/csv`, { responseType: 'blob' }),
  downloadIxbrl: (reportSlug) => api.get(`/reports/${reportSlug}/ixbrl`, { responseType: 'blob' }),
};

// Accrual Accounting & Provisioning
export const accountingApi = {
  getProvisioningConfig: () => api.get('/provisioning-config'),
  updateProvisioningConfig: (data) => api.put('/provisioning-config', data),
  runProvisioning: () => api.post('/accounting/run-provisioning'),
  runAccruals: () => api.post('/accounting/run-accruals'),
  getProvisioningReport: () => api.get('/accounting/provisioning-report'),
  getProvisioningHistory: (skip = 0, limit = 20) => api.get(`/accounting/provisioning-history?skip=${skip}&limit=${limit}`),
  getAccrualReport: () => api.get('/accounting/accrual-report'),
};

// Notifications
export const notificationsApi = {
  list: () => api.get('/notifications'),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

export default api;
